/* eslint-disable no-unused-vars, no-useless-escape, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaTrash, FaEllipsisV, FaPlus, FaTimes, FaSearch } from 'react-icons/fa';
import { Menu, MenuItem, Divider, IconButton } from '@mui/material';
import axios from '../../services/api';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';

const SipAccountManagement = () => {
  const { t } = useTranslation();
  // States
  const [deviceStatus, setDeviceStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [category, setCategory] = useState('all');
  const [searchKey, setSearchKey] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',          // 'all', 'webrtc', 'sip'
    assignmentStatus: 'all',  // 'all', 'assigned', 'unassigned'
    status: 'all',            // Add status filter
    searchTerm: ''           // search text
  });
  const [sortOrder, setSortOrder] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);

  const [newAccount, setNewAccount] = useState({
    username: '',
    password: '',
    type: 'sip',
    domain: 'im.qrtalkie.com',
    max_contacts: 4,  // default value
    transferAcc: '',
    transferAddress: '',
    transferPwd: ''
  });

  const [bulkCreateFields, setBulkCreateFields] = useState({
    prefix: '2001',
    from: '001',
    to: '100'
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    username: null
  });

  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const getRootDomain = () => {
    const url = window.location.hostname;
    const matches = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/:]+)/i);
    return matches && matches[1] ? matches[1] : null;
  };
  // const domain = getRootDomain();
  // const domain = "im.qrtalkie.com";

  useEffect(() => {
    fetchSipAccounts();
  }, [filters]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchSipAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/sip_account/accounts', {
        params: filters
      });
      const newAccount = response.data;
      if (!sortOrder) {
        newAccount.sort((a, b) => a?.username - b?.username);
        setSortOrder('desc');
      }
      setAccounts(newAccount);
    } catch (error) {
      toast.error(t('Failed to fetch SIP accounts'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchSipAccounts();
    setIsRefreshing(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleMenuClick = (event, account) => {
    setAnchorEl(event.currentTarget);
    setSelectedAccount(account);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAccount(null);
  };

  const validateForm = () => {
    if (!newAccount.username) {
      toast.error(t('Username is required'));
      return false;
    }
    if (newAccount.username.length < 4) {
      toast.error(t('Username must be at least 4 characters'));
      return false;
    }
    if (!newAccount.password) {
      toast.error(t('Password is required'));
      return false;
    }
    if (newAccount.password.length < 6) {
      toast.error(t('Password must be at least 6 characters'));
      return false;
    }

    if (!newAccount.domain) {
      toast.error(t('Domain is required'));
      return false;
    }

    if (newAccount?.type === 'sip' && newAccount?.transferAcc) {
      if (!newAccount?.transferAddress) {
        toast.error(t('Transfer Address is required'));
        return false;
      }

      if (!newAccount?.transferPwd) {
        toast.error(t('Transfer Password is required'));
        return false;
      }

      if (newAccount.transferPwd.length < 6) {
        toast.error(t('Transfer Password must be at least 6 characters'));
        return false;
      }
    }
    return true;
  };

  const validateBulkCreateForm = () => {
    if (!bulkCreateFields.prefix) {
      toast.error(t('Prefix is required'));
      return false;
    }

    if (bulkCreateFields.from.length !== bulkCreateFields.to.length) {
      toast.error(t('From and To values must have the same number of digits'));
      return false;
    }

    const fromValue = parseInt(bulkCreateFields.from);
    const toValue = parseInt(bulkCreateFields.to);
    if (toValue <= fromValue) {
      toast.error(t('To value must be greater than From value'));
      return false;
    }

    if (!newAccount.password) {
      toast.error(t('Password is required'));
      return false;
    }
    if (newAccount.password.length < 6) {
      toast.error(t('Password must be at least 6 characters'));
      return false;
    }

    if (!newAccount.domain) {
      toast.error(t('Domain is required'));
      return false;
    }

    if (newAccount?.type === 'sip' && newAccount?.transferAcc) {
      if (!newAccount?.transferAddress) {
        toast.error(t('Transfer Address is required'));
        return false;
      }

      if (!newAccount?.transferPwd) {
        toast.error(t('Transfer Password is required'));
        return false;
      }

      if (newAccount.transferPwd.length < 6) {
        toast.error(t('Transfer Password must be at least 6 characters'));
        return false;
      }
    }
    return true;
  };

  const handleCreateAccount = async () => {
    if (!validateForm()) return;

    try {
      await axios.post('/sip_account/accounts', { ...newAccount });
      setIsCreateModalOpen(false);
      fetchSipAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  const handleBulkCreateAccount = async () => {
    if (!validateBulkCreateForm()) return;
    const newValue = { ...bulkCreateFields, ...newAccount };
    try {
      const res = await axios.post('/sip_account/accountsBulk', { ...newValue });
      toast.success(t('Successfully created count_SIP accounts', { count_SIP: res.data.count ?? 0 }));
      setIsBulkCreateModalOpen(false);
      fetchSipAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  const handleDelete = (username) => {
    setDeleteModal({ isOpen: true, username });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/sip_account/accounts/${deleteModal.username}`);
      fetchSipAccounts();
      toast.success(t('SIP account deleted successfully'));
      setDeleteModal({ isOpen: false, username: null });
    } catch (error) {
      console.error(error);
    }
  };
  const handleCreate = () => {
    setModalMode('create');
    setNewAccount({
      username: '',
      password: '',
      //domain: 'im.qrtalkie.com',
      domain: 'sip.qrtalkie.org',
      type: 'sip',
      max_contacts: 4,  // default value
      transferAcc: '',
      transferAddress: '',
      transferPwd: ''
    });
    setIsCreateModalOpen(true);
  };

  const handleBulkCreate = () => {
    setModalMode('create');
    setNewAccount({
      username: '',
      password: '',
      //domain: 'im.qrtalkie.com',
      domain: 'sip.qrtalkie.org',
      type: 'sip',
      max_contacts: 4,  // default value
      transferAcc: '',
      transferAddress: '',
      transferPwd: ''
    });
    setBulkCreateFields({
      prefix: '2001',
      from: '001',
      to: '100'
    });
    setIsBulkCreateModalOpen(true);
  };

  const handleEdit = () => {
    handleMenuClose();
    setNewAccount({
      username: selectedAccount.username,
      password: selectedAccount.password,
      type: selectedAccount.type,
      max_contacts: selectedAccount.max_contacts,
      transferAcc: selectedAccount?.transferAcc ?? '',
      transferAddress: selectedAccount?.transferAddress ?? '',
      transferPwd: selectedAccount?.transferPwd ?? '',
      domain: selectedAccount?.domain ?? '',
    });
    setModalMode('edit');
    setIsCreateModalOpen(true);
  };

  const handleUpdateAccount = async () => {
    if (!validateForm()) return;

    try {
      await axios.post(`/sip_account/accountsUpdate`, newAccount);
      setIsCreateModalOpen(false);
      fetchSipAccounts();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSortByAccount = useCallback(() => {
    setAccounts((prev) => {
      const newAccount = [...prev];
      if (!sortOrder || sortOrder === 'asc') {
        newAccount.sort((a, b) => a?.username - b?.username);
        setSortOrder('desc');
      } else {
        newAccount.sort((a, b) => b?.username - a?.username);
        setSortOrder('asc');
      }
      return newAccount;
    });
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, sortOrder]
  );

  return (
    <div className="p-6 bg-gradient-to-br from-skin-base to-skin-secondary container-height app-text">
      {/* Filter Bar - keeping all filters but with enhanced design */}
      <div className="flex gap-4 justify-between items-center bg-skin-primary p-4 rounded-xl shadow-lg h-filter">
        <select
          className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors"
          value={filters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
        >
          <option value="all">{t('All Type')}</option>
          <option value="webrtc">{t('WebRTC')}</option>
          <option value="sip">{t('SIP')}</option>
        </select>

        <select
          className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors"
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="all">{t('All Status')}</option>
          <option value="online">{t('Online')}</option>
          <option value="offline">{t('Offline')}</option>
        </select>
        <div className="flex-1 relative" style={{ display: 'flex' }}>
          {/* <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-skin-base/50" /> */}
          <div className="relative" style={{ width: '100%' }}>
            <input
              type="text"
              placeholder={t('Search')}
              className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors w-full"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleFilterChange('searchTerm', searchKey);
                }
              }}
            />
            {searchKey && (
              <>
                <button
                  onClick={() => {
                    handleFilterChange('searchTerm', searchKey);
                  }}
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-skin-base/50 hover:text-skin-base"
                >
                  <FaSearch className="w-4 h-4" />
                </button>

                <button
                  onClick={() => {
                    setSearchKey('');
                    handleFilterChange('searchTerm', '');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-skin-base/50 hover:text-skin-base"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          {/* <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="bg-skin-base hover:bg-skin-hover px-4 py-2 rounded-lg flex items-center gap-2
                    text-skin-base transition-colors disabled:opacity-50"
          >
            <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button> */}
        </div>

        <button
          onClick={() => handleCreate()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <FaPlus />
          {t('Create')}
        </button>

        <button
          onClick={() => handleBulkCreate()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <FaPlus />
          {t('bulk_create')}
        </button>
      </div>

      {/* Stats Bar with minimal design */}
      <div className="h-title flex items-center">
        <div className="flex items-center gap-2">
          <span className="text-skin-muted">{t('Number of Accounts')}:</span>
          <span className="font-semibold">{accounts.length}</span>
        </div>
      </div>

      {/* Table with enhanced design */}
      <div className="relative overflow-x-auto overflow-y-auto h-table-height-title">
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              {/* Fixed Account column */}
              <th
                className="sticky left-0 z-20 bg-skin-primary px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[200px] cursor-pointer"
                onClick={handleSortByAccount}
              >
                {t('Account')}
              </th>
              {/* Scrollable columns */}
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">{t('Type')}</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[200px]">{t('Max Contacts')}</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">{t('Status')}</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">{t('Assigned To')}</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">{t('Creator')}</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">{t('Create Date')}</th>
              {/* Fixed Actions column */}
              <th className="sticky right-0 z-40 bg-skin-primary px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account, index) => (
              <tr key={index} className="border-t border-skin-base/10">
                {/* Fixed Account column */}
                <td className="sticky left-0 z-10 bg-skin-primary px-6 py-4 text-sm text-skin-base">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white mr-3">
                      {account.username?.slice(0, 2) ?? ''}
                    </div>
                    {account.username}
                  </div>
                </td>
                {/* Scrollable columns */}
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs min-w-[80px] inline-block text-center ${account.webrtc === 'yes'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'
                    }`}>
                    {account.type === 'webrtc' ? t('WebRTC') : t('SIP')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">{account.max_contacts}</td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap text-center">
                  <span className={`px-2 py-1 rounded-full text-xs ${account.status === 'ONLINE'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}>
                    {t(account.status)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs ${account.isAssigned
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}>
                    {account.isAssigned ? t('Assigned') : t('Unassigned')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">{account.creator || '-'}</td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {account.createdAt && account.createdAt !== '-' ?
                    new Date(account.createdAt).toISOString().split('T')[0]
                    : '-'}
                </td>
                {/* Fixed Actions column */}
                <td className="sticky right-0 z-10 bg-skin-primary px-6 py-4">
                  <div className="flex justify-center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, account)}
                      className="hover:bg-skin-hover"
                    >
                      <FaEllipsisV />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-skin-accent mx-auto" />
          </div>
        )}
      </div>

      {/* Keep existing Menu component */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            minWidth: 120,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            '& .MuiMenuItem-root': {
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: 'var(--bg-hover)'
              }
            }
          }
        }}
      >
        <MenuItem onClick={handleEdit}>
          <div className="flex items-center app-text">
            <FaEdit className="w-4 h-4 mr-2" />
            {t('Edit')}
          </div>
        </MenuItem>
        <Divider sx={{ margin: '4px 0', borderColor: 'var(--border-color)' }} />
        <MenuItem
          onClick={() => handleDelete(selectedAccount.username)}
          sx={{ color: '#ef4444' }}
        >
          <div className="flex items-center app-text">
            <FaTrash className="w-4 h-4 mr-2" />
            {t('Delete')}
          </div>
        </MenuItem>
      </Menu>

      {/* Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-6 z-50">
          <div className="bg-white w-[calc(85vh*0.7)] h-[85vh] rounded-lg shadow-xl">
            {/* Black header */}
            <div className="bg-black text-white px-4 py-3 rounded-t-lg">
              <h2 className="text-lg font-semibold">
                {modalMode === 'create' ? t('Create Account') : t('Edit Account')}
              </h2>
            </div>

            {/* White content area */}
            <div className="p-4 h-[calc(85vh-116px)] overflow-y-auto">
              <div className="space-y-4">
                {/* Form fields with black text */}
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Username')}
                  </label>
                  <input
                    type="text"
                    value={newAccount.username}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('SIP Domain')}
                  </label>
                  <input
                    type="text"
                    onChange={(e) => setNewAccount(prev => ({ ...prev, domain: e.target.value }))}
                    value={newAccount?.domain}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  // style={{ background: 'lightgrey' }}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Password')}
                  </label>
                  <input
                    type="text"
                    value={newAccount.password}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Max Contacts')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newAccount.max_contacts}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, max_contacts: Math.max(1, parseInt(e.target.value)) }))}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* {newAccount?.type === "sip" && */}
                <>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">
                      {t('Transfer Name')}
                    </label>
                    <input
                      type="text"
                      value={newAccount.transferAcc}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, transferAcc: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">
                      {t('Transfer Address')}
                    </label>
                    <input
                      type="text"
                      value={newAccount.transferAddress}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, transferAddress: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">
                      {t('Transfer Password')}
                    </label>
                    <input
                      type="text"
                      value={newAccount.transferPwd}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, transferPwd: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                </>
                {/* } */}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Category')}
                  </label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="sip"
                        checked={newAccount.type === 'sip'}
                        onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value }))}
                        className="form-radio h-4 w-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-black">{t('SIP')}</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="webrtc"
                        checked={newAccount.type === 'webrtc'}
                        onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value }))}
                        className="form-radio h-4 w-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-black">{t('WebRTC')}</span>
                    </label>
                  </div>
                </div>


              </div>
            </div>

            {/* Footer with buttons */}
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={modalMode === 'create' ? handleCreateAccount : handleUpdateAccount}
                  className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {modalMode === 'create' ? t('Create') : t('Update')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* bulk create modal */}
      {isBulkCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-6 z-50">
          <div className="bg-white w-[calc(85vh*0.7)] h-[85vh] rounded-lg shadow-xl">
            {/* Black header */}
            <div className="bg-black text-white px-4 py-3 rounded-t-lg">
              <h2 className="text-lg font-semibold">
                {t('Bulk Create Account')}
              </h2>
            </div>

            {/* White content area */}
            <div className="p-4 h-[calc(85vh-116px)] overflow-y-auto">
              <div className="space-y-4">
                {/* Form fields with black text */}
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Username Range')}
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-black">{t('Prefix')}</label>
                      <input
                        type="text"
                        value={bulkCreateFields.prefix}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setBulkCreateFields(prev => ({ ...prev, prefix: value }));
                        }}
                        className="w-20 bg-white border border-gray-300 rounded-lg px-3 py-2
                                 text-black focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-black">{t('From')}</label>
                      <input
                        type="text"
                        value={bulkCreateFields.from}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setBulkCreateFields(prev => ({ ...prev, from: value }));
                        }}
                        className="w-20 bg-white border border-gray-300 rounded-lg px-3 py-2
                                 text-black focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-black">{t('To')}</label>
                      <input
                        type="text"
                        value={bulkCreateFields.to}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setBulkCreateFields(prev => ({ ...prev, to: value }));
                        }}
                        className="w-20 bg-white border border-gray-300 rounded-lg px-3 py-2
                                 text-black focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('SIP Domain')}
                  </label>
                  <input
                    type="text"
                    onChange={(e) => setNewAccount(prev => ({ ...prev, domain: e.target.value }))}
                    value={newAccount?.domain}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  // style={{ background: 'lightgrey' }}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Password')}
                  </label>
                  <input
                    type="text"
                    value={newAccount.password}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Max Contacts')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newAccount.max_contacts}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, max_contacts: Math.max(1, parseInt(e.target.value)) }))}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* {newAccount?.type === "sip" && */}
                <>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">
                      {t('Transfer Name')}
                    </label>
                    <input
                      type="text"
                      value={newAccount.transferAcc}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, transferAcc: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">
                      {t('Transfer Address')}
                    </label>
                    <input
                      type="text"
                      value={newAccount.transferAddress}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, transferAddress: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-black mb-1">
                      {t('Transfer Password')}
                    </label>
                    <input
                      type="text"
                      value={newAccount.transferPwd}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, transferPwd: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2
                             text-black focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                </>
                {/* } */}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-black mb-1">
                    {t('Category')}
                  </label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="sip"
                        checked={newAccount.type === 'sip'}
                        onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value }))}
                        className="form-radio h-4 w-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-black">{t('SIP')}</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="webrtc"
                        checked={newAccount.type === 'webrtc'}
                        onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value }))}
                        className="form-radio h-4 w-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-black">{t('WebRTC')}</span>
                    </label>
                  </div>
                </div>


              </div>
            </div>

            {/* Footer with buttons */}
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsBulkCreateModalOpen(false)}
                  className="px-4 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={handleBulkCreateAccount}
                  className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {t('Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Replace the custom delete modal with ConfirmModal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, username: null })}
        onConfirm={confirmDelete}
        title={t('Delete Account')}
        message={`${t('Are you sure you want to delete account')}"${deleteModal.username}"?`}
      />
    </div>
  );
};

export default SipAccountManagement;
